// App.js
import React, { Suspense, lazy, useEffect, useMemo } from 'react';

// Google Maps
import { loadGoogleMaps } from 'googleMapsLoader';

// Stripe
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

// Routing
import { BrowserRouter as Router, Navigate, Routes, Route } from 'react-router-dom';
import Home from 'home/Home';
import Main from 'main/Main';
import Root from 'root/Root';

// Context
import { useTheme } from 'context/ThemeContext';
import { ThemeProvider } from 'context/ThemeContext';
import AppProviders from 'context/AppProviders';
import { useUser } from 'context/UserContext';

// Toast
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// CSS
import 'App.css';

// Utilities
import { hexToRgba } from 'utilities/Colors';

// Components
const Admin = lazy(() => import('admin/Admin'));
const DatabasePage = lazy(() => import('pages/DatabasePage'));
const Calendar = lazy(() => import('calendar/Calendar'));
const Channels = lazy(() => import('channels/Channel'));
const DashboardPage = lazy(() => import('pages/DashboardPage'));
const Notifications = lazy(() => import('notifications/Notifications'));
const Favorites = lazy(() => import('favorites/Favorites'));
const LayoutPage = lazy(() => import('pages/layout/LayoutPage'));
const PublicPortalRoute = lazy(() => import('pages/PublicPortalRoute'));
const Trash = lazy(() => import('trash/Trash'));
const NodePage = lazy(() => import('pages/NodePage'));
const Search = lazy(() => import('search/Search'));
const Store = lazy(() => import('pages/Store'));
const Template = lazy(() => import('pages/Template'));
const Api = lazy(() => import('pages/Api'));
const ProfilePage = lazy(() => import('pages/ProfilePage'));
const WorkspaceInvitePage = lazy(() => import('pages/WorkspaceInvitePage'));
const AuthResetPasswordPage = lazy(() => import('pages/AuthResetPasswordPage'));

const stripePromise = loadStripe('pk_test_51RmjweCOI01JFCsicVW1a1UK20GR8ShGxe71bJdqAzsRg5kbtAuLkMlgtBW6PlkqTfI65RZcJDEBTFVuSH0T3hmd00Q14luEjd');

const SuperUserAdminRoute = () => {
  const {
    user,
    isAuthLoading,
    isAuthClaimsLoading,
    isSuperUser,
  } = useUser();

  if (isAuthLoading || isAuthClaimsLoading) {
    return null;
  }

  if (!user || !isSuperUser) {
    return <Navigate to="/" replace />;
  }

  return <Admin />;
};

function App() {

  useEffect(() => {
    const preloadMaps = () => {
      loadGoogleMaps()
        .catch((error) => console.error(error));
    };

    // Defer non-critical third-party script work until browser idle time.
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(preloadMaps, { timeout: 5000 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(preloadMaps, 2000);
    return () => window.clearTimeout(timeoutId);
  }, []);

  return (

    <Elements stripe={stripePromise}>
      <AppProviders>
        <Router>
          <div className="App-root">
            <ThemeConsumer>
              <Suspense>
                <Routes>

                  <Route path="/p/:workspacePath/:nodePath/:portalPath" element={<PublicPortalRoute />} />
                  <Route path="/auth/reset-password" element={<AuthResetPasswordPage />} />

                  <Route path="/" element={<Root />}>
                    <Route path="admin" element={<SuperUserAdminRoute />} />

                    <Route element={<Main />}>
                      <Route index element={<Home />} />
                      <Route path="invite" element={<WorkspaceInvitePage />} />
                      <Route path="calendar" element={<Calendar />} />
                      <Route path="channels" element={<Channels />} />
                      <Route path="channels/:id" element={<Channels />} />
                      <Route path="notifications" element={<Notifications />} />
                      <Route path="favorites" element={<Favorites />} />
                      <Route path="trash" element={<Trash />} />
                      <Route path="search" element={<Search />} />
                      <Route path="store" element={<Store />} />
                      <Route path="api" element={<Api />} />
                      <Route path="workspace/:workspaceKey/profile/:memberId" element={<ProfilePage />} />
                      <Route path="template" element={<Template />} />
                      <Route path="template/:id" element={<Template />} />
                      <Route path="node/:id" element={<NodePage />} />
                      <Route path="database/:id" element={<DatabasePage />} />
                      <Route path="dashboard/:id" element={<DashboardPage />} />
                      <Route path="layout/:id" element={<LayoutPage />} />
                    </Route>

                    {/* Fallback route */}
                    <Route path="*" element={<div>Not found</div>} />
                  </Route>
                </Routes>
              </Suspense>
            </ThemeConsumer>
          </div>
        </Router>
      </AppProviders>
    </Elements>
  );
}

// Provides the theme context based on the current app
const ThemeConsumer = ({ children }) => {

  return (
    <ThemeProvider>
      <AppThemeVariables />
      <ThemedToastContainer />
      {children}
    </ThemeProvider>
  );
};

/**
 * AppThemeVariables
 *
 * Publishes the current ThemeContext values as reusable global CSS custom properties.
 *
 * @returns {null} No rendered output.
 */
const AppThemeVariables = () => {
  const { theme } = useTheme();

  const themeVariables = useMemo(() => ({
    '--color-bg': theme.background,
    '--color-bg-hover': theme.backgroundHover,
    '--color-bg-muted': theme.backgroundMuted,
    '--color-bg-mobile': theme.backgroundMobile,
    '--color-fg': theme.foreground,
    '--color-fg-outline': theme.outline,
    '--color-fg-outline-muted': theme.outlineMuted,
    '--color-fg-muted': theme.foregroundMuted,
    '--color-panel': theme.panel,
    '--color-highlight': theme.highlight,
    '--color-highlight-fg': theme.highlightForeground,
    '--color-overlay': theme.overlay,
    '--color-danger': theme.red,
    '--color-border': theme.backgroundMuted,
    '--color-border-strong': theme.outline,
    '--surface-input': theme.input,
    '--surface-input-hover': theme.backgroundHover,
    '--surface-panel': theme.panel,
    '--surface-popup': theme.popup,
    '--button-bg': theme.buttonBackgroundColor,
    '--button-bg-hover': theme.buttonHoverBackgroundColor,
    '--button-fg': theme.buttonLabelColor,
    '--button-fg-hover': theme.buttonHoverLabelColor,
    '--button-icon': theme.buttonIconColor,
    '--button-icon-hover': theme.buttonHoverIconColor,
    '--panel-bg': hexToRgba(theme.background, 0.91),
    '--panel-bg-soft': hexToRgba(theme.background, 0.8),
    '--panel-border': theme.backgroundMuted,
    '--panel-shadow': `0 24px 80px ${hexToRgba(theme.foreground, 0.12)}`,
    '--panel-shadow-soft': hexToRgba(theme.foreground, 0.12),
    '--panel-blur': '24px',
    '--panel-focus-ring': `0 0 0 6px ${hexToRgba(theme.highlight, 0.14)}`,
    '--panel-focus-ring-color': hexToRgba(theme.highlight, 0.14),
    '--panel-hero-glow': `radial-gradient(circle, ${hexToRgba(theme.highlight, 0.14)} 0%, transparent 72%)`,
    '--screen-hero-bg': `radial-gradient(circle at top, ${hexToRgba(theme.highlight, 0.12)} 0%, ${theme.background} 52%, ${theme.background} 100%)`,
  }), [theme]);

  useEffect(() => {
    const rootStyle = document.documentElement?.style;

    if (!rootStyle) {
      return undefined;
    }

    Object.entries(themeVariables).forEach(([name, value]) => {
      rootStyle.setProperty(name, value);
    });

    return () => {
      Object.keys(themeVariables).forEach((name) => {
        rootStyle.removeProperty(name);
      });
    };
  }, [themeVariables]);

  return null;
};

const ThemedToastContainer = () => {
  const { theme } = useTheme();

  return (
    <div
      style={{
        '--toast-bg-default': theme.background,
        '--toast-bg-info': theme.blue,
        '--toast-bg-success': theme.success,
        '--toast-bg-warning': theme.warning,
        '--toast-bg-error': theme.warning,
      }}
    >
      <ToastContainer
        autoClose={3000}
        transition={Slide}
        hideProgressBar={true}
        pauseOnHover={false}
        pauseOnFocusLoss={false}
        closeOnClick={false}
      />
    </div>
  );
};

export default App;
